import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import { type ContractService, errors, paxRoute } from "../../http";
import { basemapRoutes } from "./paths";
import {
  BeginRoomRequest,
  FreezeRoomRequest,
  RoomConnection,
  RoomIdParams,
  RoomSnapshot,
  ThawRoomRequest,
} from "./schemas";

export const service = {
  group: "basemaps",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Asset Editor API",
  description:
    "Each basemap draft's room: its base, its commits, the people in it, and its freeze for publication.",
} as const satisfies ContractService;

const serviceAuth = {
  kind: "service",
  detail: "Requires an API service access token with the basemaps:manage scope.",
  caller: "api",
  scope: "basemaps:manage",
} as const;
const refusals = { ...errors.unauthorized, ...errors.forbidden } as const;
const Done = z.strictObject({});

export const connect = oc
  .meta(
    paxRoute({
      auth: {
        kind: "session",
        detail:
          "Requires the browser's sign-in cookie. The asset editor has the Product API confirm it owns or collaborates on the draft's asset.",
      },
      policy: { session: true },
    }),
    openapi({
      method: "POST",
      path: basemapRoutes.connect,
      operationId: "basemaps.v1alpha1.connect",
      summary: "Connect to a basemap draft.",
      description: "Wakes the draft's room, and mints a short-lived token for its socket.",
      inputStructure: "detailed",
      successStatus: 201,
    }),
  )
  .errors({
    ...errors.badRequest,
    UNAUTHORIZED: { message: "Sign in first." },
    FORBIDDEN: { message: "This account is banned." },
    NOT_FOUND: { message: "There's no draft with that id that you can edit." },
    SERVICE_UNAVAILABLE: { message: "The asset editor couldn't ask the Pax API who you are." },
  })
  .input(z.object({ params: RoomIdParams }))
  .output(RoomConnection);

export const begin = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "PUT",
      path: basemapRoutes.room,
      operationId: "basemaps.v1alpha1.begin",
      summary: "Begin a basemap draft's room.",
      description:
        "Gives a new draft its first document and its base, the package it begins from. Repeating it changes nothing once it has begun.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals })
  .input(z.object({ params: RoomIdParams, body: BeginRoomRequest }))
  .output(RoomSnapshot);

export const snapshot = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "GET",
      path: basemapRoutes.snapshot,
      operationId: "basemaps.v1alpha1.snapshot",
      summary: "Read a basemap draft as it is.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound })
  .input(z.object({ params: RoomIdParams }))
  .output(RoomSnapshot);

export const freeze = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "POST",
      path: basemapRoutes.freeze,
      operationId: "basemaps.v1alpha1.freeze",
      summary: "Freeze a basemap draft for publication.",
      description:
        "Stops the draft taking commits and says what it is. Freezing again for the same publication says the same.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound, ...errors.conflict })
  .input(z.object({ params: RoomIdParams, body: FreezeRoomRequest }))
  .output(RoomSnapshot);

export const thaw = oc
  .meta(
    paxRoute({ auth: serviceAuth }),
    openapi({
      method: "POST",
      path: basemapRoutes.thaw,
      operationId: "basemaps.v1alpha1.thaw",
      summary: "Let a frozen basemap draft take commits again.",
      description:
        "After a publication that failed, says why, and takes commits again. After one that worked, the draft is a published version, and its room closes for good.",
      inputStructure: "detailed",
    }),
  )
  .errors({ ...errors.badRequest, ...refusals, ...errors.notFound })
  .input(z.object({ params: RoomIdParams, body: ThawRoomRequest }))
  .output(Done);
